import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';

/**
 * LucvanHeader - Header unificado limpio y moderno
 * Props:
 * - title: string (módulo, ej: "Clínica", "Producción", "Taller")
 * - user: { name: string, email: string, role?: string }
 * - onBack: function (opcional, callback para botón volver)
 * - onLogout: function (callback para cerrar sesión)
 * - showBack: boolean (mostrar botón volver)
 * - notifications: array (notificaciones para la campana)
 * - unreadCount: number (cantidad de notificaciones sin leer)
 * - onMarkAsRead: function (callback para marcar como leída)
 * - onClearAll: function (callback para limpiar todas)
 * - isConnected: boolean (estado de conexión WebSocket)
 */
export default function LucvanHeader({ 
  title, 
  user, 
  onBack, 
  onLogout, 
  showBack = false, 
  children,
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onClearAll,
  isConnected = false
}) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    onLogout();
  };

  return (
    <header className="bg-gradient-to-r from-[#0066A4] to-[#003C63] shadow-lg">
      <div className="max-w-full px-6 py-4">
        <div className="flex justify-between items-center">
          {/* IZQUIERDA: Botón Atrás */}
          <div className="flex-shrink-0">
            {showBack ? (
              <button
                onClick={onBack}
                className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition font-medium flex items-center gap-2"
              >
                ← Volver
              </button>
            ) : (
              <div className="w-20"></div>
            )}
          </div>

          {/* CENTRO: Logo y Título */}
          <div 
            className="flex flex-col items-center cursor-pointer flex-1"
            onClick={() => navigate('/')}
          >
            <img 
              src="/lucvan-logo-web.png" 
              alt="Lucván" 
              className="h-12 brightness-0 invert"
            />
            {title && (
              <p className="text-xs text-white/80 font-semibold mt-1 tracking-wider">
                {title}
              </p>
            )}
          </div>

          {/* DERECHA: Notificaciones y Usuario */}
          <div className="flex-shrink-0 flex items-center gap-4">
            {/* Campana de Notificaciones (solo si hay callback) */}
            {onMarkAsRead && (
              <NotificationBell 
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={onMarkAsRead}
                onClearAll={onClearAll}
                isConnected={isConnected}
              />
            )}

            {/* Dropdown de Usuario */}
            {(user?.name || user?.email) && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition"
                >
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                    {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs font-semibold text-white leading-tight">
                      {user?.name || user?.email?.split('@')[0]}
                    </p>
                    {user?.role && (
                      <p className="text-[10px] text-white/70 uppercase">
                        {user?.role === 'workshop' ? 'Producción' : user?.role}
                      </p>
                    )}
                  </div>
                  <svg
                    className={`w-4 h-4 text-white transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50 overflow-hidden border border-gray-200">
                    {/* Info Usuario */}
                    <div className="bg-gradient-to-r from-[#0066A4] to-[#003C63] text-white px-4 py-3">
                      <p className="font-semibold text-sm">{user?.name || 'Usuario'}</p>
                      <p className="text-xs text-white/80 truncate">{user?.email}</p>
                      {user?.role && (
                        <p className="text-xs text-white/70 uppercase mt-1">
                          {user?.role === 'workshop' ? 'Producción' : user?.role}
                        </p>
                      )}
                    </div>

                    {/* Botón Cerrar Sesión */}
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-3 text-left text-sm font-semibold text-gray-700 hover:bg-red-50 border-t border-gray-200 transition flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
